import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Bookmark, BookmarkCheck } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BookmarkButton = ({ reviewId, showText = false, className = '' }) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated && reviewId) {
      checkBookmarkStatus();
    }
  }, [isAuthenticated, reviewId]);

  const checkBookmarkStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/bookmarks/check/${reviewId}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setIsBookmarked(data.bookmarked);
      }
    } catch (error) {
      console.error('Failed to check bookmark status:', error);
    }
  };

  const toggleBookmark = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      if (isBookmarked) {
        const response = await fetch(`${API_URL}/api/bookmarks/${reviewId}`, {
          method: 'DELETE',
          credentials: 'include',
        });
        if (response.ok) {
          setIsBookmarked(false);
        }
      } else {
        const response = await fetch(`${API_URL}/api/bookmarks`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ review_id: reviewId }),
        });
        if (response.ok) {
          setIsBookmarked(true);
        }
      }
    } catch (error) {
      console.error('Failed to toggle bookmark:', error);
    } finally {
      setLoading(false);
    }
  };

  const Icon = isBookmarked ? BookmarkCheck : Bookmark;
  const label = isBookmarked ? 'Remove from Saved' : 'Save Review';

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={toggleBookmark}
            disabled={loading}
            className={`flex items-center gap-2 px-3 py-2 border transition-all disabled:opacity-50 ${
              isBookmarked
                ? 'bg-[#52ea88]/10 border-[#52ea88]/30 text-[#52ea88]'
                : 'bg-[#121917] border-[#52ea88]/10 text-[#9FBFB4] hover:border-[#52ea88]/30 hover:text-[#E6FFF6]'
            } ${className}`}
          >
            {loading ? (
              <div className="w-4 h-4 border border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <Icon className="w-4 h-4" />
            )}
            {showText && <span className="text-sm">{isBookmarked ? 'Saved' : 'Save'}</span>}
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="bg-[#0B0F0E] border border-[#52ea88]/30 text-[#E6FFF6] text-xs p-2"
        >
          {label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default BookmarkButton;
